import { MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useViewOnly } from '@/components/templates/app-shell'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { displayAssociationTime } from '@/lib/dates'
import type { ConnectionDetail, ConnectionType, SyncRun } from './detailTypes'

// Every header except a removed Connection's shows the filled "Sync now".
export const headerShowsFilledButton = (row: ConnectionDetail) => row.status !== 'removed'

export function ConnectionDetailHeader({
  row,
  type,
  runs,
  pending,
  onAction,
}: {
  row: ConnectionDetail
  type?: ConnectionType
  runs: SyncRun[]
  pending: boolean
  onAction: (action: string) => void
}) {
  const viewOnly = useViewOnly()
  pending = pending || viewOnly
  if (!headerShowsFilledButton(row))
    return (
      <Button variant="outline" disabled={pending} onClick={() => onAction('restore')}>
        Restore
      </Button>
    )
  const running = runs.find((run) => run.status === 'running')
  const paused = row.status === 'paused'
  const unavailable = row.status === 'unavailable'
  return (
    <div className="flex flex-wrap items-start justify-end gap-2">
      <div className="flex flex-col gap-1">
        <Button
          disabled={pending || !!running || paused || unavailable}
          onClick={() => onAction('sync_now')}
        >
          Sync now
        </Button>
        {running ? (
          <p className="text-caption text-muted-foreground">
            Running since {displayAssociationTime(running.started_at)}
          </p>
        ) : (
          paused && <p className="text-caption text-muted-foreground">Resume to sync</p>
        )}
      </div>
      <Button
        variant="outline"
        disabled={pending || unavailable}
        onClick={() => onAction(paused ? 'resume' : 'pause')}
      >
        {paused ? 'Resume' : 'Pause'}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="More" disabled={pending}>
            <MoreHorizontal className="size-4" strokeWidth={1.5} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => onAction('rename')}>Rename</DropdownMenuItem>
          <DropdownMenuItem disabled={!type || unavailable} onSelect={() => onAction('reconnect')}>
            {type?.credential_method === 'oauth' ? 'Reconnect' : 'Rotate key'}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onAction('import')}>
            Edit import-from date
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive disabled={unavailable} onSelect={() => onAction('remove')}>
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
